import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, openEventStream } from './api.js';
import { Login } from './components/Login.jsx';
import { Header } from './components/Header.jsx';
import { EndpointsSidebar } from './components/EndpointsSidebar.jsx';
import { EndpointBar } from './components/EndpointBar.jsx';
import { FilterBar, RequestList, EmptyState } from './components/RequestList.jsx';
import { DetailPanel, EmptyDetail } from './components/DetailPanel.jsx';
import { Toaster, toast } from './components/Toaster.jsx';

export default function App() {
  // Bootstrap: ask the server who we are. 401 means we need to log in.
  // Anything else (500, network failure, …) we surface as an error so the
  // user can retry instead of crashing the tree with a null user.
  const [boot, setBoot] = useState({ status: 'loading', user: null, baseUrl: '', error: null });

  const fetchMe = () => {
    setBoot((b) => ({ ...b, status: 'loading', error: null }));
    return api.me()
      .then((r) => setBoot({ status: 'in', user: r.user, baseUrl: r.baseUrl, error: null }))
      .catch((err) => {
        if (err.status === 401) setBoot({ status: 'out', user: null, baseUrl: '', error: null });
        else setBoot({ status: 'error', user: null, baseUrl: '', error: err.message || 'Server is unreachable' });
      });
  };

  useEffect(() => { fetchMe(); }, []);

  const onLogout = async () => {
    try { await api.logout(); } catch (_) {}
    setBoot({ status: 'out', user: null, baseUrl: '', error: null });
  };

  if (boot.status === 'loading') return <Splash />;
  if (boot.status === 'error') return <BackendError message={boot.error} onRetry={fetchMe} />;
  if (boot.status === 'out')   return <Login onLogin={() => fetchMe()} />;
  if (!boot.user)              return <BackendError message="No user returned from server." onRetry={fetchMe} />;
  return <Main user={boot.user} baseUrl={boot.baseUrl} onLogout={onLogout} />;
}

function BackendError({ message, onRetry }) {
  return (
    <div className="login-shell">
      <div className="login-card">
        <h1>Can't reach the server</h1>
        <p>{message || 'The backend returned an error.'}</p>
        <p style={{ fontSize: 12, color: 'var(--fg-4)', marginBottom: 16 }}>
          Check the terminal running <code>npm run dev</code> for details.
          Most common: Postgres isn't running, or <code>DATABASE_URL</code> isn't set.
        </p>
        <button className="btn btn-primary" onClick={onRetry} style={{ width: '100%', justifyContent: 'center' }}>
          Retry
        </button>
      </div>
    </div>
  );
}

function Splash() {
  return (
    <div style={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--fg-3)', fontSize: 13,
    }}>Loading…</div>
  );
}

// ---------------------------------------------------------------------------

function Main({ user, baseUrl, onLogout }) {
  const [endpoints, setEndpoints] = useState([]);
  const [activeEndpointId, setActiveEndpointId] = useState(null);
  const [eventsByEndpoint, setEventsByEndpoint] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [newIds, setNewIds] = useState(() => new Set());
  const [filters, setFilters] = useState({ q: '', status: '' });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [payloadView, setPayloadView] = useState('tree');

  const eventsForActive = eventsByEndpoint[activeEndpointId] || [];

  // ---------- Initial load ----------
  useEffect(() => {
    api.listEndpoints().then((r) => {
      setEndpoints(r.endpoints);
      if (r.endpoints.length && !activeEndpointId) setActiveEndpointId(r.endpoints[0].id);
    }).catch((err) => {
      console.error(err);
      toast('Failed to load endpoints', { error: true });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Load events when active endpoint changes ----------
  useEffect(() => {
    if (!activeEndpointId) return;
    if (eventsByEndpoint[activeEndpointId]) return; // already loaded
    api.listEvents(activeEndpointId).then((r) => {
      setEventsByEndpoint(prev => ({ ...prev, [activeEndpointId]: r.events }));
    }).catch(() => {
      toast('Failed to load events', { error: true });
    });
  }, [activeEndpointId, eventsByEndpoint]);

  // ---------- SSE: one stream per known endpoint, fans into events map ----------
  // Track which endpoints are paused in a ref so the (stable) SSE handlers can
  // drop events the instant the user hits pause — without waiting for the
  // PATCH round-trip. The server also gates broadcasts on `paused`, but events
  // already in flight when pause is clicked would otherwise still slip through.
  const pausedRef = useRef(new Set());
  useEffect(() => {
    pausedRef.current = new Set(endpoints.filter(e => e.paused).map(e => e.id));
  }, [endpoints]);

  const subsRef = useRef(new Map());
  useEffect(() => {
    const wanted = new Set(endpoints.map(e => e.id));
    // Close streams for endpoints that have been deleted.
    for (const [id, close] of subsRef.current) {
      if (!wanted.has(id)) { close(); subsRef.current.delete(id); }
    }
    // Open streams for new endpoints.
    for (const ep of endpoints) {
      if (subsRef.current.has(ep.id)) continue;
      const close = openEventStream(ep.id, (evt) => {
        if (pausedRef.current.has(ep.id)) return; // paused — drop incoming events
        setEventsByEndpoint(prev => {
          const cur = prev[ep.id] || [];
          // De-dupe in case the same event arrives via initial load and SSE.
          if (cur.some(e => e.id === evt.id)) return prev;
          return { ...prev, [ep.id]: [evt, ...cur].slice(0, 200) };
        });
        setEndpoints(prev => prev.map(e => e.id === ep.id ? { ...e, eventCount: (e.eventCount || 0) + 1 } : e));
        setNewIds(s => { const n = new Set(s); n.add(evt.id); return n; });
        setTimeout(() => setNewIds(s => { const n = new Set(s); n.delete(evt.id); return n; }), 1400);
      });
      subsRef.current.set(ep.id, close);
    }
    return () => { /* stream cleanup happens via wanted-set on next pass and on unmount */ };
  }, [endpoints]);

  useEffect(() => () => {
    for (const close of subsRef.current.values()) close();
    subsRef.current.clear();
  }, []);

  // ---------- Mutations ----------
  const onCreateEndpoint = useCallback(async (draft) => {
    try {
      const r = await api.createEndpoint(draft);
      setEndpoints(prev => [...prev, { ...r.endpoint, eventCount: 0 }]);
      setActiveEndpointId(r.endpoint.id);
      setEventsByEndpoint(prev => ({ ...prev, [r.endpoint.id]: [] }));
    } catch (err) {
      toast(`Could not create endpoint: ${err.message}`, { error: true });
    }
  }, []);

  const onDeleteEndpoint = useCallback(async (id) => {
    try {
      await api.deleteEndpoint(id);
      setEndpoints(prev => {
        const next = prev.filter(e => e.id !== id);
        if (activeEndpointId === id) setActiveEndpointId(next[0]?.id ?? null);
        return next;
      });
      setEventsByEndpoint(prev => { const n = { ...prev }; delete n[id]; return n; });
    } catch (err) { toast(`Delete failed: ${err.message}`, { error: true }); }
  }, [activeEndpointId]);

  const onReorderEndpoints = useCallback(async (next) => {
    setEndpoints(next);
    try { await api.reorderEndpoints(next.map(e => e.id)); }
    catch (err) { toast(`Reorder failed: ${err.message}`, { error: true }); }
  }, []);

  const onUpdateEndpoint = useCallback(async (id, patch) => {
    setEndpoints(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
    try { await api.updateEndpoint(id, patch); }
    catch (err) { toast(`Update failed: ${err.message}`, { error: true }); }
  }, []);

  const onRegenerateEndpoint = useCallback(async (id) => {
    try {
      const r = await api.regenerateEndpoint(id);
      setEndpoints(prev => prev.map(e => e.id === id ? { ...e, ...r.endpoint } : e));
      // Remap cached events to the new id.
      setEventsByEndpoint(prev => {
        const cached = prev[id];
        if (!cached) return prev;
        const next = { ...prev };
        delete next[id];
        next[r.endpoint.id] = cached.map(e => ({ ...e, endpointId: r.endpoint.id }));
        return next;
      });
      if (activeEndpointId === id) setActiveEndpointId(r.endpoint.id);
      toast('URL regenerated. Old URL stops working.');
    } catch (err) { toast(`Regenerate failed: ${err.message}`, { error: true }); }
  }, [activeEndpointId]);

  const onToggleStreaming = useCallback(async () => {
    if (!activeEndpointId) return;
    const ep = endpoints.find(e => e.id === activeEndpointId);
    if (!ep) return;
    onUpdateEndpoint(ep.id, { paused: !ep.paused });
  }, [activeEndpointId, endpoints, onUpdateEndpoint]);

  const onTogglePinEvent = useCallback(async (eventId) => {
    const cur = (eventsByEndpoint[activeEndpointId] || []).find(e => e.id === eventId);
    if (!cur) return;
    const pinned = !cur.pinned;
    setEventsByEndpoint(prev => ({
      ...prev,
      [activeEndpointId]: prev[activeEndpointId].map(e => e.id === eventId ? { ...e, pinned } : e),
    }));
    try { await api.pinEvent(eventId, pinned); }
    catch (err) {
      // Roll back on failure.
      setEventsByEndpoint(prev => ({
        ...prev,
        [activeEndpointId]: prev[activeEndpointId].map(e => e.id === eventId ? { ...e, pinned: !pinned } : e),
      }));
      toast(`Pin failed: ${err.message}`, { error: true });
    }
  }, [activeEndpointId, eventsByEndpoint]);

  const onDeleteEvent = useCallback(async (eventId) => {
    try {
      await api.deleteEvent(eventId);
      setEventsByEndpoint(prev => ({
        ...prev,
        [activeEndpointId]: (prev[activeEndpointId] || []).filter(e => e.id !== eventId),
      }));
      setEndpoints(prev => prev.map(e => e.id === activeEndpointId ? { ...e, eventCount: Math.max(0, (e.eventCount || 1) - 1) } : e));
      setSelectedId(prev => prev === eventId ? null : prev);
    } catch (err) { toast(`Delete failed: ${err.message}`, { error: true }); }
  }, [activeEndpointId]);

  const onClearAllEvents = useCallback(async () => {
    if (!activeEndpointId) return;
    if (!confirm('Delete all unpinned events on this endpoint?')) return;
    try {
      await api.clearEvents(activeEndpointId);
      setEventsByEndpoint(prev => ({
        ...prev,
        [activeEndpointId]: (prev[activeEndpointId] || []).filter(e => e.pinned),
      }));
      setEndpoints(prev => prev.map(e => e.id === activeEndpointId
        ? { ...e, eventCount: (eventsByEndpoint[activeEndpointId] || []).filter(x => x.pinned).length }
        : e));
    } catch (err) { toast(`Clear failed: ${err.message}`, { error: true }); }
  }, [activeEndpointId, eventsByEndpoint]);

  // ---------- Filtering ----------
  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return eventsForActive.filter(e => {
      if (filters.status === '2xx' && !(e.responseCode >= 200 && e.responseCode < 300)) return false;
      if (filters.status === '4xx' && !(e.responseCode >= 400 && e.responseCode < 500)) return false;
      if (filters.status === '5xx' && !(e.responseCode >= 500)) return false;
      if (q) {
        const hay = (
          (e.id || '') + ' ' + (e.method || '') + ' ' + e.responseCode + ' ' +
          (e.payload != null ? JSON.stringify(e.payload) : (e.rawBody || '')) + ' ' +
          Object.entries(e.headers || {}).map(([k, v]) => `${k}:${v}`).join(' ') + ' ' +
          (e.ip || '')
        ).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [eventsForActive, filters]);

  const selectedEvent = eventsForActive.find(e => e.id === selectedId) || null;
  useEffect(() => {
    if (!selectedEvent && filtered.length > 0) setSelectedId(filtered[0].id);
  }, [filtered, selectedEvent]);
  useEffect(() => { setSelectedId(null); }, [activeEndpointId]);

  const activeEndpoint = endpoints.find(e => e.id === activeEndpointId) || null;
  const showEmpty = activeEndpoint && eventsForActive.length === 0;
  const pinnedCount = eventsForActive.filter(e => e.pinned).length;

  return (
    <>
      <Header
        user={user}
        endpoints={endpoints}
        onCreateEndpoint={onCreateEndpoint}
        onLogout={onLogout}
      />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <EndpointsSidebar
          endpoints={endpoints}
          activeId={activeEndpointId}
          setActiveId={setActiveEndpointId}
          onDeleteEndpoint={onDeleteEndpoint}
          onReorderEndpoints={onReorderEndpoints}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {!activeEndpoint ? (
            <NoEndpoint />
          ) : (
            <>
              <EndpointBar
                endpoint={activeEndpoint}
                baseUrl={baseUrl}
                count={activeEndpoint.eventCount ?? eventsForActive.length}
                events={eventsForActive}
                onToggleStreaming={onToggleStreaming}
                onRegenerate={() => onRegenerateEndpoint(activeEndpoint.id)}
                onUpdate={(patch) => onUpdateEndpoint(activeEndpoint.id, patch)}
              />

              {!showEmpty && (
                <FilterBar
                  filters={filters} setFilters={setFilters}
                  events={eventsForActive}
                  count={eventsForActive.length}
                  pinnedCount={pinnedCount}
                  onClearAll={onClearAllEvents}
                />
              )}

              <div style={{
                margin: '16px 24px 24px',
                flex: 1, minHeight: 420,
                display: 'flex',
                border: '1px solid var(--border-default)', borderRadius: 12,
                background: '#fff', overflow: 'hidden',
                boxShadow: 'var(--shadow-xs)',
              }}>
                <div style={{
                  width: 500, minWidth: 360, maxWidth: '55%',
                  borderRight: '1px solid var(--border-default)',
                  display: 'flex', flexDirection: 'column', background: '#fff',
                }}>
                  {showEmpty ? (
                    <EmptyState baseUrl={baseUrl} endpointId={activeEndpoint.id} />
                  ) : (
                    <RequestList
                      events={eventsForActive}
                      filtered={filtered}
                      density="compact"
                      selectedId={selectedId}
                      setSelectedId={setSelectedId}
                      onDelete={onDeleteEvent}
                      onTogglePin={onTogglePinEvent}
                      newIds={newIds}
                    />
                  )}
                </div>
                {selectedEvent ? (
                  <DetailPanel
                    event={selectedEvent}
                    baseUrl={baseUrl}
                    onClose={() => setSelectedId(null)}
                    onTogglePin={() => onTogglePinEvent(selectedEvent.id)}
                    payloadView={payloadView}
                    setPayloadView={setPayloadView}
                  />
                ) : <EmptyDetail />}
              </div>
            </>
          )}
        </div>
      </div>
      <Toaster />
    </>
  );
}

function NoEndpoint() {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: 48, textAlign: 'center', color: 'var(--fg-3)',
    }}>
      <h2 style={{ fontSize: 20, color: 'var(--fg-1)', marginBottom: 8 }}>Welcome to Hooks</h2>
      <p style={{ fontSize: 14, maxWidth: 460, margin: 0 }}>
        Click <strong>+ New endpoint</strong> in the header to generate a webhook URL,
        then point any payment provider at it. Captured requests will stream in here in real time.
      </p>
    </div>
  );
}
